import { useUIStore } from '@/config/stores/ui-store';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import type { ConversationCreate } from '@/features/conversation/models/conversation-dto';
import type { User } from '@/features/participant';
import { UserAutocomplete } from '@/features/participant';
import { BackButton, GradientAvatar } from '@/shared/components';
import { useCreateConversation } from '../queries/conversation.mutations';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { useCallback } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { MAX_GROUP_NAME_LENGTH } from '../constants';
import { useConversationAdd } from '../hooks/use-conversation-add';
import { useConversationForm } from '../hooks/use-conversation-form';
import { UserListSelected } from './user-list-selected';

export const ConversationCreateDialog: React.FC = () => {
  // Hooks
  const { t } = useTranslation();
  const { setActiveConversation } = useUIStore();
  const { user } = useAuthStore();
  const { mutateAsync: createConversation, isPending } = useCreateConversation();

  const { handleSubmit, control, selectedUsers, setSelectedUsers, hasUnsavedChanges, handleRemoveUser, handleReset } =
    useConversationForm(user);

  const { show, handleClose: handleDialogClose } = useConversationAdd();

  const onValid = useCallback(
    async (data: ConversationCreate) => {
      try {
        const conv: ConversationCreate = {
          ...data,
          conversationType: 'GROUP'
        };

        const res = await createConversation(conv);
        setActiveConversation(res);

        handleReset();
        handleDialogClose(false, handleReset);
      } catch (error) {
        console.error('Failed to create conversation:', error);
        toast.error(t('conversations.toasts.createError'));
      }
    },
    [createConversation, handleReset, handleDialogClose, setActiveConversation]
  );

  const handleClose = useCallback(() => {
    handleDialogClose(hasUnsavedChanges, handleReset);
  }, [handleDialogClose, hasUnsavedChanges, handleReset]);

  const handleUserSelect = useCallback(
    (user: User) => {
      if (!selectedUsers.find((u) => u.id === user.id)) {
        setSelectedUsers([...selectedUsers, user]);
      }
    },
    [selectedUsers, setSelectedUsers]
  );

  return (
    <Dialog
      id='conversation-add-dialog'
      component='form'
      onSubmit={handleSubmit(onValid)}
      open={show}
      fullWidth
      maxWidth='md'
      onClose={handleClose}
    >
      <DialogTitle variant='h6'>{t('conversations.create.dialogTitle')}</DialogTitle>
      <DialogContent>
        <Stack gap={3} flexDirection='column' sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Section 1: Avatar và Tên nhóm trên cùng một dòng */}
          <Stack direction='row' gap={2} alignItems='end'>
            <Controller
              control={control}
              name='file'
              render={({ field: { onChange } }) => (
                <GradientAvatar
                  sx={{
                    width: 72,
                    height: 72,
                    bgcolor: 'primary.main',
                    flexShrink: 0
                  }}
                  onChange={(file) => onChange(file)}
                  enableUpload
                />
              )}
            />
            <Controller
              control={control}
              name='conversationName'
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <TextField
                  variant='outlined'
                  size='small'
                  fullWidth
                  value={value}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    if (newValue.length <= MAX_GROUP_NAME_LENGTH) {
                      onChange(newValue);
                    }
                  }}
                  placeholder={t('conversations.create.namePlaceholder')}
                  error={Boolean(error)}
                  helperText={error?.message || `${value.length}/${MAX_GROUP_NAME_LENGTH} ký tự`}
                  slotProps={{
                    htmlInput: {
                      maxLength: MAX_GROUP_NAME_LENGTH
                    }
                  }}
                  sx={{ flex: 1 }}
                />
              )}
            />
          </Stack>

          <Divider />

          {/* Section 2: Thêm thành viên với Autocomplete */}
          <Box>
            <Typography variant='subtitle2' className='mex:!mb-2 mex:!font-medium'>
              {t('conversations.members.addButton')}
            </Typography>
            <UserAutocomplete
              selectedUsers={selectedUsers}
              onUserSelect={handleUserSelect}
              placeholder={t('conversations.create.membersPlaceholder')}
              excludeUserIds={user?.user_id ? [user.user_id] : []}
            />
            {selectedUsers.length === 0 && (
              <Typography variant='caption' color='text.secondary' className='mex:!mt-1 mex:!block'>
                {t('conversations.validation.membersRequired')}
              </Typography>
            )}
          </Box>

          {/* Section 3: Danh sách thành viên đã chọn */}
          <Divider />
          <UserListSelected selects={selectedUsers} onRemove={handleRemoveUser} maxHeight={200} />
        </Stack>
      </DialogContent>

      <DialogActions className='mex:px-6 mex:pb-4'>
        <BackButton onClick={handleClose}>{t('conversations.create.cancelButton')}</BackButton>
        <Button
          variant='contained'
          color='primary'
          className='mex:!min-w-28'
          type='submit'
          disabled={isPending || selectedUsers.length === 0}
          loading={isPending}
        >
          {isPending ? t('conversations.create.creating') : t('conversations.create.submitButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
